import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import BulkSetupWizard from '../components/BulkSetupWizardNew';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import {
  CubeIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BoltIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

ChartJS.register(ArcElement, Tooltip, Legend);

const DashboardPageNew = () => {
  const { isAuthenticated, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_products: 0,
    total_competitors: 0,
    total_pricing_rules: 0,
    recent_price_changes: 0,
    price_positions: {
      highest_price: 0,
      higher_than_average: 0,
      cheaper_than_average: 0,
      cheapest: 0
    },
    rule_status: {
      ok: 0,
      warning: 0,
      no_rule: 0
    }
  });
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positionProducts, setPositionProducts] = useState([]);
  const [selectedRuleStatus, setSelectedRuleStatus] = useState(null);
  const [ruleStatusProducts, setRuleStatusProducts] = useState([]);
  const [showBulkWizard, setShowBulkWizard] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = getToken();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/dashboard/stats`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStats(response.data);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        console.error('Error fetching dashboard stats:', err);
      }
    };

    fetchStats();
  }, [getToken]);

  const refreshDashboard = async () => {
    try {
      const token = getToken();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/dashboard/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Error refreshing dashboard:', err);
    }
  };

  const getImageUrl = (product) => {
    if (product.photo?.md) return product.photo.md;
    if (product.photo?.sm) return product.photo.sm;
    if (product.photo?.medium) return product.photo.medium;
    if (product.photo?.small) return product.photo.small;
    return '/placeholder.png';
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-12 w-40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, trend }) => (
    <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <div className="flex items-baseline gap-2">
            <h3 className="text-3xl font-bold">{value}</h3>
            {trend && (
              <Badge variant={trend > 0 ? "default" : "secondary"} className="text-xs">
                {trend > 0 ? '+' : ''}{trend}%
              </Badge>
            )}
          </div>
        </div>
        <div className={`p-4 rounded-full ${color}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </Card>
  );

  const RuleStatusCard = ({ status, count, label, description, color, icon }) => {
    const isSelected = selectedRuleStatus === status;

    const handleClick = async () => {
      const newStatus = status === selectedRuleStatus ? null : status;
      setSelectedRuleStatus(newStatus);
      setRuleStatusProducts([]);

      if (newStatus && count > 0) {
        try {
          const token = getToken();
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/dashboard/products?rule_status=${newStatus}&limit=10`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setRuleStatusProducts(response.data);
        } catch (error) {
          console.error('Error fetching products:', error);
        }
      }
    };

    return (
      <Card
        className={`p-4 cursor-pointer hover:shadow-md transition-all duration-200 ${
          isSelected ? 'ring-2 ring-primary' : ''
        }`}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${color} ${status === 'warning' ? 'animate-pulse' : ''}`} />
            <div>
              <p className="font-semibold text-gray-800">{icon} {label}</p>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          </div>
          <span className="text-2xl font-bold">{count}</span>
        </div>
      </Card>
    );
  };

  const PricePositionCard = ({ title, count, color, icon: Icon, position }) => {
    const isSelected = selectedPosition === position;
    const percentage = stats.total_products > 0
      ? ((count / stats.total_products) * 100).toFixed(1)
      : 0;

    const handleClick = async () => {
      if (count === 0) return;

      const newPosition = position === selectedPosition ? null : position;
      setSelectedPosition(newPosition);

      if (newPosition) {
        try {
          const token = getToken();
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/dashboard/products?position=${newPosition}&limit=10`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPositionProducts(response.data || []);
        } catch (error) {
          console.error('Error fetching products:', error);
          setPositionProducts([]);
        }
      }
    };

    return (
      <Card
        className={`p-6 transition-all duration-200 ${
          count > 0 ? 'cursor-pointer hover:shadow-md hover:-translate-y-1' : 'opacity-60'
        } ${isSelected ? 'ring-2 ring-primary' : ''}`}
        onClick={handleClick}
      >
        <div className="flex items-center justify-between mb-3">
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <Badge variant="outline" className="text-xs">
            {percentage}%
          </Badge>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-2xl font-bold">{count}</p>
        </div>
        <Progress value={parseFloat(percentage)} className="mt-3 h-2" />
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">داشبورد</h1>
          <p className="text-muted-foreground mt-1">خلاصه وضعیت محصولات و قیمت‌گذاری</p>
        </div>
        <button
          onClick={() => setShowBulkWizard(true)}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition font-bold shadow-lg hover:shadow-xl"
        >
          <BoltIcon className="h-5 w-5" />
          <span>تنظیم یکجا</span>
        </button>
      </div>

      <BulkSetupWizard
        isOpen={showBulkWizard}
        onClose={() => setShowBulkWizard(false)}
        onComplete={refreshDashboard}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="تعداد کل محصولات"
          value={stats.total_products}
          icon={CubeIcon}
          color="bg-blue-100 text-blue-600"
        />
        <StatCard
          title="تعداد کل رقبا"
          value={stats.total_competitors}
          icon={UsersIcon}
          color="bg-green-100 text-green-600"
        />
        <StatCard
          title="قوانین قیمت‌گذاری"
          value={stats.total_pricing_rules}
          icon={ClipboardDocumentListIcon}
          color="bg-purple-100 text-purple-600"
        />
        <StatCard
          title="تغییرات (24 ساعت)"
          value={stats.recent_price_changes}
          icon={ArrowTrendingUpIcon}
          color="bg-orange-100 text-orange-600"
        />
      </div>

      {/* Rule Status Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ChartBarIcon className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">وضعیت قوانین قیمت‌گذاری</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <RuleStatusCard
            status="ok"
            count={stats.rule_status.ok}
            label="مناسب"
            description="قیمت مطابق قانون"
            color="bg-green-500"
            icon="✅"
          />
          <RuleStatusCard
            status="warning"
            count={stats.rule_status.warning}
            label="نیاز به توجه"
            description="قانون نقض شده"
            color="bg-red-500"
            icon="⚠️"
          />
          <RuleStatusCard
            status="no_rule"
            count={stats.rule_status.no_rule}
            label="بدون قانون"
            description="قانون تعریف نشده"
            color="bg-gray-400"
            icon="⚫"
          />
        </div>

        {/* Products Preview for Rule Status */}
        {selectedRuleStatus && ruleStatusProducts.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                محصولات {
                  selectedRuleStatus === 'ok' ? 'با قانون مناسب' :
                  selectedRuleStatus === 'warning' ? 'با هشدار' :
                  'بدون قانون'
                }
              </h3>
              <button
                onClick={() => navigate('/products')}
                className="text-sm text-primary hover:underline"
              >
                مشاهده همه →
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ruleStatusProducts.map((product) => (
                <Card
                  key={product.id}
                  className="p-4 cursor-pointer hover:shadow-md transition"
                  onClick={() => navigate(`/competitors/${product.id}`)}
                >
                  <div className="flex items-start gap-3">
                    <img
                      src={getImageUrl(product)}
                      alt={product.title}
                      className="w-20 h-20 object-cover rounded"
                      onError={(e) => {
                        e.target.src = '/placeholder.png';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm line-clamp-2 mb-2">
                        {product.title}
                      </h4>
                      <p className="text-base font-bold text-primary">
                        {Math.round(product.price / 10).toLocaleString('fa-IR')} تومان
                      </p>
                      {product.rule_name && (
                        <Badge variant="outline" className="mt-2 text-xs">
                          {product.rule_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Price Position Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ArrowTrendingUpIcon className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">موقعیت قیمت نسبت به رقبا</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PricePositionCard
                title="بالاترین قیمت"
                count={stats.price_positions.highest_price}
                color="bg-red-100 text-red-600"
                icon={ArrowTrendingUpIcon}
                position="highest"
              />
              <PricePositionCard
                title="بالاتر از میانگین"
                count={stats.price_positions.higher_than_average}
                color="bg-orange-100 text-orange-600"
                icon={ArrowTrendingUpIcon}
                position="higher"
              />
              <PricePositionCard
                title="پایین‌تر از میانگین"
                count={stats.price_positions.cheaper_than_average}
                color="bg-green-100 text-green-600"
                icon={ArrowTrendingDownIcon}
                position="cheaper"
              />
              <PricePositionCard
                title="ارزان‌ترین"
                count={stats.price_positions.cheapest}
                color="bg-blue-100 text-blue-600"
                icon={ArrowTrendingDownIcon}
                position="cheapest"
              />
            </div>
          </div>

          <Card className="p-6 flex items-center justify-center">
            <div className="w-full max-w-xs">
              <Doughnut
                data={{
                  labels: ['بالاترین', 'بالاتر', 'پایین‌تر', 'ارزان‌ترین'],
                  datasets: [{
                    data: [
                      stats.price_positions.highest_price,
                      stats.price_positions.higher_than_average,
                      stats.price_positions.cheaper_than_average,
                      stats.price_positions.cheapest
                    ],
                    backgroundColor: ['#ef4444', '#f97316', '#10b981', '#3b82f6'],
                    borderWidth: 0
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        font: { size: 11 },
                        padding: 10,
                        usePointStyle: true
                      }
                    }
                  }
                }}
              />
            </div>
          </Card>
        </div>

        {/* Products Preview for Price Position */}
        {selectedPosition && positionProducts.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                محصولات {
                  selectedPosition === 'highest' ? 'با بالاترین قیمت' :
                  selectedPosition === 'higher' ? 'بالاتر از میانگین' :
                  selectedPosition === 'cheaper' ? 'پایین‌تر از میانگین' :
                  'ارزان‌ترین'
                }
              </h3>
              <button
                onClick={() => navigate('/products')}
                className="text-sm text-primary hover:underline"
              >
                مشاهده همه →
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {positionProducts.map((product) => (
                <Card
                  key={product.id}
                  className="p-4 cursor-pointer hover:shadow-md transition"
                  onClick={() => navigate(`/competitors/${product.id}`)}
                >
                  <div className="flex items-start gap-3">
                    <img
                      src={getImageUrl(product)}
                      alt={product.title}
                      className="w-20 h-20 object-cover rounded"
                      onError={(e) => {
                        e.target.src = '/placeholder.png';
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm line-clamp-2 mb-2">
                        {product.title}
                      </h4>
                      <p className="text-base font-bold text-primary mb-1">
                        {Math.round(product.price / 10).toLocaleString('fa-IR')} تومان
                      </p>
                      <p className="text-xs text-muted-foreground">
                        📦 موجودی: {product.inventory}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DashboardPageNew;
